import { bandConfigFor } from '../config/generation-config';
import { SHAPE_TEMPLATES } from '../model/shape';
import { mulberry32 } from './rng';
import { frameworkBandRamp, targetScoreForLevel } from './difficulty-curve';
/** Recipe RNG stream — deliberately distinct from the board-building stream. */
const RECIPE_SALT = 625341585;
const RECIPE_OFFSET = 461845907;
/**
 * Stride between seed variants (WP-35.12's seed explorer). Variant 0 is the
 * shipped level; any other number re-draws the recipe for the SAME level spec,
 * which is what makes "give me 20 different boards for this band" possible
 * without moving the level.
 */
const VARIANT_STRIDE = 2654435761;
/**
 * How many recipes to draw before settling. A redraw is cheap (a handful of
 * weighted picks); this only has to be large enough to find a non-repeating draw
 * when one exists, and small enough that an IMPOSSIBLE constraint (one unlocked
 * shape, or a band whose colour budget is the whole palette) costs nothing much.
 */
const RECIPE_TRIES = 24;
// ─── Shapes ───────────────────────────────────────────────────────────────────
/** The shape families unlocked at this level, with their weight for this band. */
export function unlockedShapes(config, levelNumber, band) {
    return config.shapes.families
        .filter((f) => (config.unlocks.shapes[f.id] ?? 1) <= levelNumber)
        .map((f) => ({ id: f.id, weight: f.weightPerBand[band] }))
        .filter((f) => f.weight > 0);
}
/** The level a family is configured to unlock at (1 = available from the start). */
export function configuredUnlockLevel(config, familyId) {
    return config.unlocks.shapes[familyId] ?? 1;
}
/**
 * The first level a family can ACTUALLY be drawn on (WP-35.14).
 *
 * Availability is the AND of two config sections that are easy to leave
 * disagreeing: `unlocks.shapes` (a level) and `shapes.families[].weightPerBand`
 * (a per-band number). Measured before this WP, the shipped config had `1x4`
 * unlocking at level 25 but zero weight in the band covering levels 1–25, so it
 * first appeared on level **71** — and the L, unlocked at 60, first appeared on
 * **151**. This function is what makes that discrepancy visible instead of
 * silently shipping a schedule nobody honours.
 *
 * Returns `null` when a family can never appear at all.
 */
export function effectiveUnlockLevel(config, familyId, maxScan = 500) {
    const configured = configuredUnlockLevel(config, familyId);
    for (let level = configured; level <= maxScan; level++) {
        const band = targetScoreForLevel(config, level).difficulty;
        if (unlockedShapes(config, level, band).some((s) => s.id === familyId))
            return level;
    }
    return null;
}
/** The whole shape schedule, configured vs effective — read by the Studio + tests. */
export function shapeSchedule(config) {
    return config.shapes.families
        .map((family) => {
        const configured = configuredUnlockLevel(config, family.id);
        const effective = effectiveUnlockLevel(config, family.id);
        return {
            id: family.id,
            configured,
            effective,
            honoured: effective === configured,
            weightPerBand: { ...family.weightPerBand },
        };
    })
        .sort((a, b) => a.configured - b.configured || a.id.localeCompare(b.id));
}
/**
 * Families whose unlock level is exactly this level — the ones a player is meeting
 * for the first time. The recipe forces one block of each (see `drawRecipe`), so
 * "unlocked at level N" means the shape is ON the board at level N, which is what
 * the unlock popup + guided first use of WP-35.16 will hang off.
 */
export function familiesUnlockingAt(config, levelNumber, band) {
    const available = new Set(unlockedShapes(config, levelNumber, band).map((s) => s.id));
    return config.shapes.families
        .filter((f) => configuredUnlockLevel(config, f.id) === levelNumber && available.has(f.id))
        .map((f) => f.id);
}
function weightedPick(entries, rng) {
    const total = entries.reduce((sum, e) => sum + e.weight, 0);
    if (total <= 0)
        return null;
    let roll = rng() * total;
    for (const e of entries) {
        roll -= e.weight;
        if (roll <= 0)
            return e.id;
    }
    return entries[entries.length - 1].id;
}
/** Deterministic Fisher–Yates — a copy, so callers keep their input. */
function shuffled(items, rng) {
    const out = items.slice();
    for (let i = out.length - 1; i > 0; i--) {
        const j = Math.floor(rng() * (i + 1));
        [out[i], out[j]] = [out[j], out[i]];
    }
    return out;
}
// ─── Density ramp (T1) ────────────────────────────────────────────────────────
/**
 * How many blocks a level gets: ramp from the band's `min` at its first level to
 * `max` at its last (WP-35.11). Before this, one number per band meant every
 * level of a band had the same density — levels 4 and 25 were both "four 1x2s".
 *
 * **The band here is a FRAMEWORK band** (Beginner…Infinite), re-keyed in WP-36.6
 * and therefore derived from the level number alone. The four gameplay bands
 * were too coarse to ramp on: `Easy` covers levels 1–25, so one ramp had to
 * serve both a player's first board and their twenty-fifth. A `difficulty`
 * override (the Studio's, say) still moves grid size, shape weights and the cell
 * ceiling — it just no longer moves the density ramp, which is a property of
 * where the level sits on the ladder.
 *
 * Two deliberate deviations:
 *  - **Level 1 is capped at `density.startBlocks`** (locked decision 12: L1 ≈ 3
 *    blocks), so raising a band's `min` in the Studio can never turn the first
 *    level a player ever sees into a crowded board.
 *  - **Recovery levels shed `density.recoveryBlockDrop` blocks**, floored at
 *    `startBlocks`. The score curve already dips on those levels (Framework §17),
 *    but density is an INPUT to the board, not something targeting can lower — so
 *    without this the dip had to come out of chance alone.
 */
export function blockCountFor(config, levelNumber) {
    let count = Math.round(frameworkBandRamp(config, levelNumber, config.density.blocksPerBand));
    if (levelNumber === 1)
        count = Math.min(count, config.density.startBlocks);
    if (targetScoreForLevel(config, levelNumber).isRecovery) {
        count = Math.max(config.density.startBlocks, count - config.density.recoveryBlockDrop);
    }
    return Math.max(1, count);
}
/**
 * How many distinct colours a level uses (WP-35.15): the same framework-band ramp
 * the block count follows, so the two climb together — 2 at level 1, the full
 * 7-colour palette deep in Infinite.
 *
 * The result is a CEILING, not a promise: a board with 3 blocks cannot show 5
 * colours, so the recipe also clamps to its block count (a colour with no block
 * would leave a gate nothing can ever exit through).
 */
export function colourCountFor(config, levelNumber) {
    const count = Math.round(frameworkBandRamp(config, levelNumber, config.colours.countPerBand));
    return Math.max(1, Math.min(count, config.colours.palette.length));
}
// ─── Recipe (T2) ──────────────────────────────────────────────────────────────
const keyOf = (values) => values.slice().sort().join('+');
/** How many recipes back the anti-repetition rules can see. */
function historyDepth(config) {
    const { noRepeatWindow, maxConsecutiveSameShape, maxConsecutiveSameColourSet } = config.antiRepetition;
    return Math.max(noRepeatWindow, maxConsecutiveSameShape, maxConsecutiveSameColourSet, 1);
}
/**
 * How many of this recipe's rules the candidate breaks, given the recipes for the
 * levels just before it. Zero is a clean draw; a non-zero count is only ever
 * settled for when no clean draw exists (e.g. level 3, where `1x2` is the only
 * unlocked shape, so "a different shape multiset" is not a thing that exists).
 */
function violationsOf(candidate, history, config) {
    const { noRepeatWindow, maxConsecutiveSameShape, maxConsecutiveSameColourSet } = config.antiRepetition;
    let violations = 0;
    const runOf = (key, value) => {
        let run = 0;
        for (let i = history.length - 1; i >= 0 && history[i][key] === value; i--)
            run++;
        return run;
    };
    if (runOf('shapeSignature', candidate.shapeSignature) + 1 > maxConsecutiveSameShape) {
        violations++;
    }
    if (runOf('colourSignature', candidate.colourSignature) + 1 > maxConsecutiveSameColourSet) {
        violations++;
    }
    if (noRepeatWindow > 0 &&
        history.slice(-noRepeatWindow).some((h) => h.signature === candidate.signature)) {
        violations++;
    }
    return violations;
}
/**
 * How many blocks may share one shape family on this level (WP-36.7).
 *
 * `shapes.maxSameFamily` (shipped: 3) exists to stop a board being four L-blocks
 * out of eight — repetitive, and expensive to place. But a cap is only meaningful
 * if it still leaves a CHOICE, and with few families unlocked it stops doing
 * that: two families and six blocks under a cap of 3 admits exactly one multiset,
 * 3+3. Measured on levels 11–14 after the WP-36.7 density retune, that is what
 * happened — all 24 recipe draws came back identical, and three consecutive
 * levels shipped the same shapes, defeating the anti-repetition rule sitting in
 * the same config.
 *
 * So the cap is raised just far enough that at least TWO distributions exist:
 * `families × cap` must exceed `blockCount`. This also subsumes the old
 * "waive the cap when exactly one family is unlocked" special case (levels 1–7
 * are all 1x2), which was the same problem with the smallest possible number of
 * families.
 */
export function sameFamilyCap(config, familyCount, blockCount) {
    if (familyCount <= 0)
        return blockCount;
    return Math.max(config.shapes.maxSameFamily, Math.ceil((blockCount + 1) / familyCount));
}
/** Draw one candidate recipe (no history check) from the given RNG stream. */
function drawRecipe(config, levelNumber, band, blockCount, rng) {
    const shapes = unlockedShapes(config, levelNumber, band);
    // A family unlocking on THIS level is guaranteed a block (WP-35.14) — otherwise
    // "unlocks at 25" only means "may start appearing around 25", and an unlock
    // popup could fire on a board that does not contain the shape it announces.
    // Introductions take the first slots, which is also what survives the
    // generator's shrink-on-failure path (it drops shapes off the END).
    const introduced = familiesUnlockingAt(config, levelNumber, band).slice(0, blockCount);
    const shapeIds = [...introduced];
    // Two ceilings the weights know nothing about (WP-35.14): total CELLS, and how
    // many blocks may share one family. An introduction is exempt from both — it
    // has to be on the board — but still counts towards them.
    const cellsOf = (id) => SHAPE_TEMPLATES[id]?.length ?? 1;
    const gridSize = bandConfigFor(config, band).gridSize;
    const cellBudget = Math.floor(gridSize * gridSize * config.density.maxCellFraction[band]);
    const perFamily = new Map();
    let cells = 0;
    for (const id of shapeIds) {
        cells += cellsOf(id);
        perFamily.set(id, (perFamily.get(id) ?? 0) + 1);
    }
    const perFamilyCap = sameFamilyCap(config, shapes.length, blockCount);
    for (let i = introduced.length; i < blockCount; i++) {
        const affordable = shapes.filter((s) => cells + cellsOf(s.id) <= cellBudget);
        const candidates = affordable.filter((s) => (perFamily.get(s.id) ?? 0) < perFamilyCap);
        // Out of room: a shorter board beats one that cannot be placed or solved.
        // (Reachable only through the CELL ceiling now — `sameFamilyCap` guarantees
        // the family cap alone can always seat `blockCount` blocks.)
        if (candidates.length === 0)
            break;
        // `1x2` is the guaranteed starter family, so an over-filtered config still
        // yields a buildable recipe rather than an empty one.
        const id = weightedPick(candidates, rng) ?? '1x2';
        shapeIds.push(id);
        cells += cellsOf(id);
        perFamily.set(id, (perFamily.get(id) ?? 0) + 1);
    }
    const palette = config.colours.palette;
    // `shapeIds.length`, not the requested `blockCount`: the cell ceiling may have
    // cut the board short, and a colour with no block would leave a gate nothing
    // can exit through.
    const budget = Math.min(colourCountFor(config, levelNumber), palette.length, shapeIds.length);
    // A random SUBSET, not the first N of the palette: taking the first N meant
    // every level of a band shipped the same colours (Blue/Green/Red/Yellow for all
    // of levels 4–25). Blocks are dealt round-robin from this list, so each chosen
    // colour is guaranteed at least one block — and therefore exactly one gate.
    const colours = shuffled(palette, rng).slice(0, Math.max(1, budget));
    const shapeSignature = keyOf(shapeIds);
    const colourSignature = keyOf(colours);
    return {
        levelNumber,
        difficulty: band,
        // What the board will actually carry, after the cell ceiling had its say.
        blockCount: shapeIds.length,
        shapeIds,
        colours,
        shapeSignature,
        colourSignature,
        signature: `${shapeSignature}#${colourSignature}`,
    };
}
/**
 * The recipe for one level, judged against `history` (most recent last). Draws up
 * to `RECIPE_TRIES` candidates and takes the first that breaks no rule; if none
 * does, the least-offending draw wins — best-effort, because the constraints are
 * genuinely unsatisfiable in places (only one shape unlocked early on; a band
 * whose colour budget is the entire palette has exactly one possible colour set).
 */
export function recipeFor(config, levelNumber, band, history = [], variant = 0) {
    const rng = mulberry32(levelNumber * RECIPE_SALT + RECIPE_OFFSET + variant * VARIANT_STRIDE);
    const blockCount = blockCountFor(config, levelNumber);
    let best = null;
    let bestViolations = Number.POSITIVE_INFINITY;
    for (let attempt = 0; attempt < RECIPE_TRIES; attempt++) {
        const candidate = drawRecipe(config, levelNumber, band, blockCount, rng);
        const violations = violationsOf(candidate, history, config);
        if (violations === 0)
            return candidate;
        if (violations < bestViolations) {
            best = candidate;
            bestViolations = violations;
        }
    }
    return best;
}
/**
 * The recipe for `levelNumber`, with its history built by replaying the chain
 * from level 1. O(level) in cheap work — no board is built and no solver runs, so
 * the whole chain for level 150 costs well under a millisecond, against a ~40ms
 * generation budget per level.
 *
 * Replaying from level 1 (rather than from `level - window`) is what makes the
 * guarantee real: the history a level is judged against is exactly the recipes
 * those levels actually ship with, not an approximation that changes depending on
 * where the chain started.
 */
export function recipeForLevel(config, levelNumber, bandOverride, variant = 0, chain) {
    const history = chain?.historyBefore(levelNumber) ?? buildHistoryBefore(config, levelNumber);
    const band = bandOverride ?? targetScoreForLevel(config, levelNumber).difficulty;
    // The history stays the levels' NATURAL recipes; only this level's draw takes
    // the variant, so a seed-explorer board is still judged against the real ladder.
    return recipeFor(config, levelNumber, band, history, variant);
}
/** The trailing window of recipes for levels 1..levelNumber-1, replayed. */
function buildHistoryBefore(config, levelNumber) {
    const depth = historyDepth(config);
    const history = [];
    for (let level = 1; level < levelNumber; level++) {
        history.push(recipeFor(config, level, targetScoreForLevel(config, level).difficulty, history));
        if (history.length > depth)
            history.shift();
    }
    return history;
}
/**
 * A memoised recipe chain (WP-36.11) — the same history `recipeForLevel` builds,
 * kept between calls so consecutive levels EXTEND it instead of replaying from
 * level 1 each time.
 *
 * **Scale, honestly:** WP-36.10 measured the replay at 0.4ms against an 80ms
 * generation at level 150 — half of one percent, not the cause of anything a
 * player feels. It is memoised because it is nearly free to do and because the
 * cost is genuinely superlinear further out (0.96ms at L200, 20ms at L500), so
 * an Infinite-band player would eventually notice a term nobody had budgeted.
 *
 * Owned and injected by `FreeLevelGenerator`, which already holds the session's
 * anti-repetition memory — no module-level state, per the module's DI rule.
 * Results are IDENTICAL to the un-memoised path; `level-recipe.test.ts` asserts
 * that level by level rather than trusting the construction.
 */
export class RecipeChain {
    constructor(config) {
        this.config = config;
        this.history = [];
        /** Highest level already folded into `history`; 0 = nothing yet. */
        this.built = 0;
        this.depth = historyDepth(config);
    }
    /**
     * The history a draw for `levelNumber` must be judged against.
     *
     * Walking FORWARD extends what is there. Walking backward (a restart, a
     * Studio jump) rebuilds from level 1 — the chain is a prefix, so it cannot be
     * truncated cheaply, and being asked for an earlier level is rare. Asking for
     * the same level twice is free and returns exactly the same window, which is
     * what makes a restart deterministic.
     */
    historyBefore(levelNumber) {
        if (levelNumber - 1 < this.built)
            this.reset();
        for (let level = this.built + 1; level < levelNumber; level++) {
            this.history.push(recipeFor(this.config, level, targetScoreForLevel(this.config, level).difficulty, this.history));
            if (this.history.length > this.depth)
                this.history.shift();
            this.built = level;
        }
        return this.history;
    }
    /** Forget the chain (config changed, or a backwards walk). */
    reset() {
        this.history.length = 0;
        this.built = 0;
    }
}
